import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  brokerQuotes,
  operations,
  quoteFinancial,
  type Operation,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatCpfCnpj, formatDateTime, formatMoney } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Icon } from '@/shared/ui/Icon';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Pagination } from '@/shared/ui/Pagination';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { statusLabel } from '@/shared/ui/statusLabels';
import { useToast } from '@/shared/ui/useToast';

import { nomeDoResumo, salvarArquivoDoFinanceiro } from '../financeiroDaCotacao';
import { PartyToolbar } from '../../shared/PartyToolbar';

import { BoletosDaCotacao } from './BoletosDaCotacao';
import { BrokerQuoteFiltersDialog } from './BrokerQuoteFiltersDialog';
import {
  filtrosDaCorretoraVazios,
  podeCancelarNaLista,
  quantosFiltrosDaCorretora,
  type BrokerQuoteFilters,
} from './cotacaoDaCorretora';
import { QuoteTypeDialog } from './QuoteTypeDialog';
import { QuoteWithdrawDialog } from './QuoteWithdrawDialog';

/** Dez por pagina, como a lista de cotacoes da corretora do OnPoint (NCT-023). */
const limite = 10;

/**
 * Lista de cotacoes da corretora (`/propostas` do OnPoint para a CORRETORA; NCT-001 a
 * NCT-038). Mesma rota da lista da seguradora, com a visao da corretora (decisao 2 da
 * 11.11).
 *
 * As colunas na ordem do OnPoint: Acoes, Nro. da Cotacao, Tipo, Canal, Tomador,
 * Segurado, Modalidade, Status, Data da cotacao e IS. O menu da linha tem Visualizar
 * cotacao, Cancelar cotacao (pela configuracao da empresa), Imprimir cotacao, que baixa o
 * resumo da cotacao do financeiro (P-COT-4, ligado pelo P-NCT-L; NCT-019), Baixar
 * boleto(s), com os boletos que a seguradora anexou as parcelas (P-BOL; NCT-057), os
 * da apolice depois da emissao (R5-A08), e
 * Imprimir minuta, desligado: a minuta e lida so pela seguradora (NCT-020, SUB-018). O
 * servidor so devolve as cotacoes da corretora da sessao.
 */
export function BrokerQuotesPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [busca, setBusca] = useState('');
  const [offset, setOffset] = useState(0);
  const [filtros, setFiltros] = useState<BrokerQuoteFilters>(filtrosDaCorretoraVazios);
  const [filtrando, setFiltrando] = useState(false);
  const [escolhendo, setEscolhendo] = useState(false);
  const [cancelando, setCancelando] = useState<Operation | null>(null);
  const [boletosDe, setBoletosDe] = useState<Operation | null>(null);
  const aplicados = quantosFiltrosDaCorretora(filtros);
  const filtrado = busca !== '' || aplicados > 0;

  const podeCriar = can('operacoes.operacao.create');
  const podeCancelar = can('operacoes.cotacao.withdraw');

  const consulta = useQuery({
    queryKey: ['operations', 'corretora', { busca, offset, filtros }],
    queryFn: ({ signal }) =>
      operations.list({ q: busca, limit: limite, offset, ...filtros }, signal),
  });
  const opcoes = useQuery({
    queryKey: ['broker-quote-options'],
    queryFn: ({ signal }) => brokerQuotes.options(signal),
    enabled: podeCancelar,
    retry: false,
  });
  const escopo = opcoes.data?.settings.broker_withdraw_scope;

  // "Imprimir cotacao": o resumo da cotacao (ObterResumoProposta), o mesmo do "Baixar
  // resumo" da linha do tempo. PDF quando a instalacao converte, Word quando nao.
  const imprimir = useMutation({
    mutationFn: (operacao: Operation) => quoteFinancial.summary(operacao.id),
    onSuccess: (arquivo, operacao) =>
      salvarArquivoDoFinanceiro(arquivo, nomeDoResumo(operacao, arquivo)),
    onError: (error: Error) => toast.error(error.message),
  });

  const numeroDe = (operacao: Operation) =>
    operacao.proposal_number ?? String(operacao.number);

  const colunas: Column<Operation>[] = [
    {
      key: 'acoes',
      header: 'Acoes',
      render: (operacao) => (
        <div className="flex flex-col items-start gap-0.5">
          <Button
            size="sm"
            variant="ghost"
            aria-label={`Visualizar cotacao ${numeroDe(operacao)}`}
            onClick={(event) => {
              event.stopPropagation();
              void navigate(`/operacoes/${operacao.id}`);
            }}
          >
            Visualizar cotacao
          </Button>
          {podeCancelar && podeCancelarNaLista(operacao.stage, escopo) ? (
            <Button
              size="sm"
              variant="ghost"
              aria-label={`Cancelar cotacao ${numeroDe(operacao)}`}
              onClick={(event) => {
                event.stopPropagation();
                setCancelando(operacao);
              }}
            >
              Cancelar cotacao
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="ghost"
            loading={imprimir.isPending && imprimir.variables?.id === operacao.id}
            aria-label={`Imprimir cotacao ${numeroDe(operacao)}`}
            onClick={(event) => {
              event.stopPropagation();
              imprimir.mutate(operacao);
            }}
          >
            Imprimir cotacao
          </Button>
          {/* A lista nao diz se ha boleto: o dialogo le as parcelas e desliga o
              download sem boleto anexado, com o motivo. A cotacao emitida abre os
              boletos da apolice. */}
          <Button
            size="sm"
            variant="ghost"
            aria-label={`Baixar boleto(s) ${numeroDe(operacao)}`}
            onClick={(event) => {
              event.stopPropagation();
              setBoletosDe(operacao);
            }}
          >
            Baixar boleto(s)
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled
            title="A minuta e lida so pela seguradora: liberar a leitura a corretora depende de decisao da seguradora (NCT-020, SUB-018)."
            aria-label={`Imprimir minuta ${numeroDe(operacao)}`}
            onClick={(event) => event.stopPropagation()}
          >
            Imprimir minuta
          </Button>
        </div>
      ),
    },
    {
      key: 'numero',
      header: 'Nro. da Cotacao',
      render: (operacao) => (
        <span className="font-medium tabular-nums text-ink-900">
          {numeroDe(operacao)}
        </span>
      ),
    },
    {
      key: 'tipo',
      header: 'Tipo',
      hideBelow: 'md',
      render: (operacao) =>
        statusLabel('tipoCotacao', operacao.proposal_type ?? 'apolice'),
    },
    {
      key: 'canal',
      header: 'Canal',
      hideBelow: 'lg',
      render: (operacao) =>
        operacao.channel?.label ?? (
          <span className="text-ink-400">Canal nao definido</span>
        ),
    },
    {
      key: 'tomador',
      header: 'Tomador',
      render: (operacao) => (
        <div className="min-w-0">
          <p className="truncate">{operacao.policyholder.label}</p>
          {operacao.policyholder.document ? (
            <p className="text-2xs tabular-nums text-ink-500">
              {formatCpfCnpj(operacao.policyholder.document)}
            </p>
          ) : null}
        </div>
      ),
    },
    {
      key: 'segurado',
      header: 'Segurado',
      hideBelow: 'md',
      render: (operacao) =>
        operacao.insured ? (
          <div className="min-w-0">
            <p className="truncate">{operacao.insured.label}</p>
            <p className="text-2xs tabular-nums text-ink-500">
              {formatCpfCnpj(operacao.insured.document)}
            </p>
          </div>
        ) : (
          <span className="text-ink-400">-</span>
        ),
    },
    {
      key: 'modalidade',
      header: 'Modalidade',
      hideBelow: 'lg',
      render: (operacao) => <span className="truncate">{operacao.modality.label}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (operacao) =>
        operacao.proposal_status ? (
          <StatusBadge kind="cotacao" value={operacao.proposal_status} />
        ) : null,
    },
    {
      key: 'data',
      header: 'Data da cotacao',
      hideBelow: 'lg',
      render: (operacao) => formatDateTime(operacao.created_at),
    },
    {
      key: 'valor',
      header: 'IS',
      align: 'right',
      render: (operacao) => formatMoney(operacao.insured_amount),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Cotacoes"
        description="As cotacoes da sua corretora, da cotacao a emissao."
        actions={
          podeCriar ? (
            <Button onClick={() => setEscolhendo(true)}>
              <Icon name="sparkle" size={15} />
              Nova cotacao ou endosso
            </Button>
          ) : null
        }
      />

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar as cotacoes">
          {consulta.error.message}
        </Alert>
      ) : null}

      <Card>
        <PartyToolbar
          search={busca}
          onSearch={(valor) => {
            setBusca(valor);
            setOffset(0);
          }}
          placeholder="Buscar por numero, objeto ou tomador"
          extra={
            <Button
              size="sm"
              variant="secondary"
              className="self-end"
              onClick={() => setFiltrando(true)}
            >
              {aplicados > 0 ? `Filtros (${aplicados})` : 'Filtros'}
            </Button>
          }
        />

        <DataTable
          columns={colunas}
          items={consulta.data?.items ?? []}
          keyOf={(operacao) => operacao.id}
          loading={consulta.isPending}
          onRowClick={(operacao) => void navigate(`/operacoes/${operacao.id}`)}
          empty={
            <EmptyState
              icon="clock"
              title={filtrado ? 'Nenhuma cotacao encontrada' : 'Nenhuma cotacao ainda'}
              description={
                filtrado
                  ? 'Ajuste os filtros para ver outras cotacoes.'
                  : 'A cotacao nasce no assistente e fica com a corretora ate o envio para analise.'
              }
              action={
                podeCriar && !filtrado ? (
                  <Button onClick={() => setEscolhendo(true)}>Nova cotacao</Button>
                ) : null
              }
            />
          }
        />

        <Pagination
          total={consulta.data?.total ?? 0}
          limit={limite}
          offset={offset}
          onChange={setOffset}
        />
      </Card>

      <BrokerQuoteFiltersDialog
        open={filtrando}
        value={filtros}
        onClose={() => setFiltrando(false)}
        onApply={(novos) => {
          setFiltros(novos);
          setOffset(0);
          setFiltrando(false);
        }}
      />

      {podeCriar ? (
        <QuoteTypeDialog open={escolhendo} onClose={() => setEscolhendo(false)} />
      ) : null}

      {cancelando ? (
        <QuoteWithdrawDialog
          operationId={cancelando.id}
          open
          onClose={() => setCancelando(null)}
        />
      ) : null}

      {boletosDe ? (
        <BoletosDaCotacao
          operationId={boletosDe.id}
          numero={numeroDe(boletosDe)}
          emitida={boletosDe.stage === 'emitida'}
          open
          onClose={() => setBoletosDe(null)}
        />
      ) : null}
    </div>
  );
}
